import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import imageCompression from 'browser-image-compression';
import { useEffect, useState } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';

import ColorPicker from '@/components/ColorPicker';
import Loader from '@/components/Loader';
import SimpleEditor from '@/components/TipTapEditorV3/SimpleEditor';
import { showErrorToast, showSuccessToast } from '@/components/ToastNotification';
import { Button } from '@/components/ui/button';
import { ImageUploadButton } from '@/components/ui/image-upload-button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { updateSettings } from '@/hooks/mutations/settingsMutations';
import { useGetSiteSettings } from '@/hooks/queries/useSiteSettings';
import { DEFAULT_LABELS } from '@/hooks/useTabLabels';

interface FormValues {
	statement: string;
	statementTitle: string;
	bannerImage: File[];
	logoImage: File[];
	color: string;
}

const Settings = () => {
	const [isSaving, setIsSaving] = useState(false);
	const [activeTab, setActiveTab] = useState('branding');
	const queryClient = useQueryClient();
	const { register, handleSubmit, control, reset } = useForm<FormValues>();
	const { data: settings, isLoading } = useGetSiteSettings();
	const [selectedColor, setSelectedColor] = useState(settings?.primary_color ?? '');
	const [bannerPreview, setBannerPreview] = useState(settings?.banner ?? '');
	const [logoPreview, setLogoPreview] = useState(settings?.logo ?? '');
	const [labelConfig, setLabelConfig] = useState<Record<string, string>>(() => ({
		...DEFAULT_LABELS,
		...settings?.label_config,
	}));
	const bannerSizeRef = 'c_scale,w_1800';
	const logoSizeRef = 'c_scale,w_600';

	const { mutate: settingsMutation } = useMutation({
		mutationFn: updateSettings,
		onSuccess: (settingsObj) => {
			showSuccessToast('Settings Updated');
			setIsSaving(false);
			// update query cache
			const globalSettingObj = queryClient.getQueryData([
				'get-settings-divisions-disciplines',
			]) as SettingsDivisionDiscipline;

			const schoolId = import.meta.env.VITE_SCHOOL_ID;
			const updatedSettings = {
				id: settingsObj?.id,
				statement: settingsObj?.statement,
				statement_title: settingsObj?.statement_title,
				primary_color: settingsObj?.primary_color,
				banner: settingsObj?.banner,
				logo: settingsObj?.logo,
				label_config: settingsObj?.label_config,
			};
			queryClient.setQueryData(['get-site-settings', schoolId], updatedSettings);

			queryClient.setQueryData(['get-settings-divisions-disciplines'], {
				...globalSettingObj,
				siteSettings: updatedSettings,
			});
		},
		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const removePreviousImage = (type: 'banner' | 'logo') => {
		const imageRef = type === 'banner' ? settings?.banner : settings?.logo;
		const pattern = `(elevate-${
			import.meta.env.VITE_CLOUDINARY_PRESET
		}|elevate-sandbox)/(.*).(png|jpg|jpeg|avif)`;
		const regex = new RegExp(pattern);
		const imageId = imageRef?.match(regex)?.[2];

		fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cloudinary-image`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
			},
			body: JSON.stringify({
				action: 'delete',
				public_id: `elevate-${import.meta.env.VITE_CLOUDINARY_PRESET}/${imageId}`,
			}),
		}).catch((error) => {
			if (import.meta.env.MODE !== 'development') {
				captureException(error);
			}
		});
	};

	const onPhotoUpload = (e: React.ChangeEvent<HTMLInputElement>, type: 'banner' | 'logo') => {
		e.preventDefault();
		const reader = new FileReader();
		const file = e?.target?.files?.[0];

		if (reader !== undefined && file !== undefined) {
			reader.onloadend = () => {
				if (type === 'banner') {
					setBannerPreview(`${reader.result}`);
				}
				if (type === 'logo') {
					setLogoPreview(`${reader.result}`);
				}
			};
			reader.readAsDataURL(file);
		}
	};

	const blobToDataURL = (blob: Blob) => {
		return new Promise<string>((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = () => reject(reader.error);
			reader.onabort = () => reject(new Error('Read aborted'));
			reader.readAsDataURL(blob);
		});
	};

	const getImageUrl = async (file: File, size: string) => {
		const options = {
			maxSizeMB: 1,
			maxWidthOrHeight: 1920,
			useWebWorker: true,
		};
		const compressedFile = await imageCompression(file, options);
		const dataUri = await blobToDataURL(compressedFile);

		try {
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cloudinary-image`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
					},
					body: JSON.stringify({
						action: 'upload',
						file: dataUri,
						folder: `elevate-${import.meta.env.VITE_CLOUDINARY_PRESET}`,
					}),
				}
			);

			if (!response.ok) {
				throw new Error('Upload failed');
			}

			const res = await response.json();
			const results = res.data?.secure_url.split('upload');
			const start = results[0];
			const end = results[1];
			return `${start}upload/${size}${end}`;
		} catch (error) {
			if (import.meta.env.MODE !== 'development') {
				captureException(error);
			}
			return 'error';
		}
	};

	const onSubmit: SubmitHandler<FormValues> = async (formData) => {
		setIsSaving(true);
		const { statement, statementTitle, bannerImage, logoImage, color } = formData;
		let bannerUrl = '';
		let logoUrl = '';

		if (bannerImage?.[0]) {
			bannerUrl = await getImageUrl(bannerImage[0], bannerSizeRef);
		}
		if (logoImage?.[0]) {
			logoUrl = await getImageUrl(logoImage[0], logoSizeRef);
		}

		// Check for upload errors before removing old images
		if ([bannerUrl, logoUrl].includes('error')) {
			setIsSaving(false);
			showErrorToast('Oh no, something went wrong... Please try again.');
			return;
		}

		// Only remove previous images after successful uploads
		if (bannerUrl) {
			removePreviousImage('banner');
		}
		if (logoUrl) {
			removePreviousImage('logo');
		}

		settingsMutation({
			id: settings?.id ?? '',
			statement,
			statementTitle,
			primaryColor: color,
			banner: bannerUrl || bannerPreview,
			logo: logoUrl || logoPreview,
			labelConfig,
		});
	};

	useEffect(() => {
		if (!settings) return;
		if (settings.primary_color) setSelectedColor(settings.primary_color);
		if (settings.banner) setBannerPreview(settings.banner);
		if (settings.logo) setLogoPreview(settings.logo);
		if (settings.label_config) setLabelConfig({ ...DEFAULT_LABELS, ...settings.label_config });
		// Seed the form state so fields in inactive tabs aren't undefined on submit
		reset({
			statement: settings.statement ?? '',
			statementTitle: settings.statement_title ?? '',
		});
	}, [settings, reset]);

	if (isLoading) return <Loader />;

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full overflow-hidden">
			<Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full min-h-0">
				<TabsList variant="line" className="w-full shrink-0 px-4">
					<TabsTrigger variant="line" value="branding">
						Branding
					</TabsTrigger>
					<TabsTrigger variant="line" value="statement">
						Statement
					</TabsTrigger>
					<TabsTrigger variant="line" value="labels">
						Course Labels
					</TabsTrigger>
				</TabsList>

				{/* ── Branding ──────────────────────────────────────────── */}
				<TabsContent value="branding" className="px-4 md:px-6">
					<div className="space-y-8 max-w-[800px]">
						<div>
							<h3 className="text-sm font-medium text-foreground mb-3">School Color</h3>
							{selectedColor && (
								<Controller
									control={control}
									name="color"
									defaultValue={selectedColor}
									render={({ field }) => (
										<ColorPicker
											onChange={(color) => {
												setSelectedColor(color.hex);
												field.onChange(color.hex);
											}}
											color={selectedColor}
										/>
									)}
								/>
							)}
						</div>

						<div>
							<h3 className="text-sm font-medium text-foreground mb-3">Banner Image</h3>
							{bannerPreview ? (
								<div
									className="bg-center bg-cover mb-4 min-h-[200px] rounded-lg"
									style={{ backgroundImage: `url(${bannerPreview})` }}
								/>
							) : (
								<img
									className="mb-4 rounded-lg"
									src="https://placehold.co/1000x200?text=No Image Selected"
									alt="placeholder"
								/>
							)}
							<ImageUploadButton
								hasPreview={!!bannerPreview}
								accept=".jpeg, .png, .jpg, .avif"
								{...register('bannerImage')}
								onChange={(e) => onPhotoUpload(e, 'banner')}
							/>
						</div>

						<div>
							<h3 className="text-sm font-medium text-foreground mb-3">School Logo</h3>
							{logoPreview ? (
								<img
									src={logoPreview}
									alt="Logo"
									className="bg-[#d9d9d9] mb-4 max-w-[300px] p-2 rounded-lg"
								/>
							) : (
								<img
									className="mb-4 rounded-lg"
									src="https://placehold.co/300x150?text=No Image Selected"
									alt="placeholder"
								/>
							)}
							<ImageUploadButton
								hasPreview={!!logoPreview}
								accept=".jpeg, .png, .jpg, .avif"
								{...register('logoImage')}
								onChange={(e) => onPhotoUpload(e, 'logo')}
							/>
						</div>
					</div>
				</TabsContent>

				{/* ── School Statement ──────────────────────────────────── */}
				<TabsContent value="statement" className="px-4 md:px-6">
					<div className="max-w-[800px] space-y-4">
						<div>
							<label htmlFor="statementTitle" className="flex typo-body-2 mb-1">
								Statement Title
							</label>
							<Input
								id="statementTitle"
								type="text"
								defaultValue={settings?.statement_title ?? ''}
								placeholder="(i.e. Our Mission)"
								{...register('statementTitle')}
							/>
						</div>
						<Controller
							control={control}
							name="statement"
							defaultValue={settings?.statement}
							render={({ field }) => (
								<SimpleEditor
									content={settings?.statement ?? ''}
									onChange={field.onChange}
									placeholder="School Statement..."
								/>
							)}
						/>
					</div>
				</TabsContent>

				{/* ── Course Labels ─────────────────────────────────────── */}
				<TabsContent value="labels" className="px-4 md:px-6">
					<p className="text-sm text-muted-foreground mb-4">
						Customize the names of course tabs and sub-sections across the application.
					</p>

					<div className="flex flex-col gap-6 max-w-[800px]">
						{(
							[
								{ tab: 'description', children: ['prereq', 'extraInfo'] },
								{ tab: 'outcomes', children: [] },
								{ tab: 'resources', children: [] },
								{ tab: 'units', children: ['question', 'topic', 'skill'] },
								{ tab: 'lessonPlans', children: [] },
							] as const
						).map(({ tab, children }) => (
							<div key={tab} className="rounded-lg border border-border p-4">
								<label htmlFor={`label-${tab}`} className="flex typo-body-2 mb-1 font-medium">
									{DEFAULT_LABELS[tab]}
								</label>
								<Input
									id={`label-${tab}`}
									type="text"
									value={labelConfig[tab] ?? ''}
									placeholder={DEFAULT_LABELS[tab]}
									onChange={(e) => setLabelConfig((prev) => ({ ...prev, [tab]: e.target.value }))}
								/>
								{children.length > 0 && (
									<div className="mt-4 pl-4 border-l-2 border-border">
										<p className="text-xs text-muted-foreground mb-2">Sub-sections</p>
										<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
											{children.map((key) => (
												<div key={key}>
													<label htmlFor={`label-${key}`} className="flex typo-body-2 mb-1 text-sm">
														{DEFAULT_LABELS[key]}
													</label>
													<Input
														id={`label-${key}`}
														type="text"
														value={labelConfig[key] ?? ''}
														placeholder={DEFAULT_LABELS[key]}
														onChange={(e) =>
															setLabelConfig((prev) => ({ ...prev, [key]: e.target.value }))
														}
													/>
												</div>
											))}
										</div>
									</div>
								)}
							</div>
						))}
					</div>
				</TabsContent>
			</Tabs>

			{/* Sticky save bar */}
			<div className="shrink-0 border-t border-border bg-background px-4 py-4 md:px-6">
				<Button type="submit" disabled={isSaving} isLoading={isSaving} className="ml-auto">
					Save Changes
				</Button>
			</div>
		</form>
	);
};

export default Settings;
